import React, { useMemo, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import type { SelectCascadingProps } from 'components/mui/Form/Select/SelectCascading/types';
import type { AutocompleteAsyncWithCheckboxesProps } from 'pages/company/DashboardRoute/pages/VendorExpensesRoute/components/Autocomplete/AutocompleteAsyncWithCheckboxes';
import Api from 'domain/api';

import { createTopVendorOptions, createPeriodOptions, PeriodValue, TopVendorValue } from './options';

export type FilterOption = {
  id: string | number;
  label: string;
};

export function useSelect<T>(defaultValue: T) {
  const [value, setValue] = useState<T>(defaultValue);

  const onChange = useCallback((_event: React.SyntheticEvent | null, newValue: T) => {
    setValue(newValue);
  }, []);

  return { value, onChange };
}

export function useMultiSelectAsyncAutocomplete<T>(fetchOptionsFunc: (searchText?: string) => Promise<T[]>) {
  const [value, setValue] = useState<T[]>([]);

  const onChange = useCallback((_event: React.SyntheticEvent | null, newValue: T[] | null) => {
    setValue(newValue || []);
  }, []);

  const fetchOptions = useCallback(
    async (searchText: string = ''): Promise<T[]> => {
      try {
        return await fetchOptionsFunc(searchText);
      } catch (error) {
        console.error('Error fetching options:', error);
        return [];
      }
    },
    [fetchOptionsFunc],
  );

  return { value, onChange, fetchOptions };
}

export const fetchCurrencyOptions = async (period: PeriodValue, searchText: string = ''): Promise<FilterOption[]> => {
  const {
    data: { options },
  } = await Api.getDashboardVendorExpensesFilterOptions({
    data: { field: 'currency', search: searchText, period },
  });
  return options;
};

export const fetchVendorOptions = async (period: PeriodValue, searchText: string = ''): Promise<FilterOption[]> => {
  const {
    data: { options },
  } = await Api.getDashboardVendorExpensesFilterOptions({
    data: { field: 'vendor', search: searchText, period },
  });
  return options;
};

const useFieldProps = () => {
  const { formatMessage } = useIntl();

  const periodOptions = useMemo(() => createPeriodOptions(formatMessage), [formatMessage]);
  const topVendorOptions = useMemo(() => createTopVendorOptions(formatMessage), [formatMessage]);

  // default is last 12 months
  const period = useSelect<PeriodValue>(periodOptions[2].value);
  // default is top 10
  const topVendors = useSelect<TopVendorValue>(topVendorOptions[0].value);

  const fetchCurrencyOptionsBound = useCallback(
    (searchText?: string) => fetchCurrencyOptions(period.value, searchText),
    [period.value],
  );
  const fetchVendorOptionsBound = useCallback(
    (searchText?: string) => fetchVendorOptions(period.value, searchText),
    [period.value],
  );

  const currencies = useMultiSelectAsyncAutocomplete<FilterOption>(fetchCurrencyOptionsBound);
  const vendors = useMultiSelectAsyncAutocomplete<FilterOption>(fetchVendorOptionsBound);

  const selectProps = useMemo(
    (): Partial<SelectCascadingProps> => ({
      FormControlProps: {
        size: 'small',
        sx: {
          width: '25ch',
          backgroundColor: 'common.white',
        },
      },
    }),
    [],
  );

  const autocompleteProps = useMemo(
    (): Partial<AutocompleteAsyncWithCheckboxesProps<FilterOption>> => ({
      size: 'small',
      disableClearable: true,
      sx: {
        width: '25ch',
        backgroundColor: 'common.white',
      },
    }),
    [],
  );

  const getCurrentValues = useCallback(
    () => ({
      period: period.value,
      currencies: currencies.value,
      vendors: vendors.value,
      topVendors: topVendors.value,
    }),
    [period.value, currencies.value, vendors.value, topVendors.value],
  );

  const vendorsPlaceholderDescriptor = useMemo(
    () =>
      vendors.value?.length
        ? { id: 'labels.vendors.vendors', defaultMessage: 'Vendors' }
        : { id: 'labels.vendors.allVendors', defaultMessage: 'All Vendors' },
    [vendors.value?.length],
  );

  return useMemo(
    () => ({
      period: {
        ...selectProps,
        id: 'period',
        label: formatMessage({ id: 'labels.period', defaultMessage: 'Period' }),
        options: periodOptions,
        value: period.value,
        onChange: period.onChange,
      },
      topVendors: {
        ...selectProps,
        id: 'topVendors',
        label: formatMessage({
          id: 'labels.vendorExpenses.topVendors',
          defaultMessage: 'Show (based on company currency)',
        }),
        options: topVendorOptions,
        value: topVendors.value,
        onChange: topVendors.onChange,
      },
      currencies: {
        ...autocompleteProps,
        fetchOptions: currencies.fetchOptions,
        value: currencies.value,
        onChange: currencies.onChange,
        label: formatMessage({ id: 'labels.currencies', defaultMessage: 'Currencies' }),
        valueKey: 'id',
        labelKey: 'label',
      },
      vendors: {
        ...autocompleteProps,
        fetchOptions: vendors.fetchOptions,
        value: vendors.value,
        onChange: vendors.onChange,
        label: formatMessage(vendorsPlaceholderDescriptor),
        valueKey: 'id',
        labelKey: 'label',
      },
      getCurrentValues,
    }),
    [
      formatMessage,
      autocompleteProps,
      selectProps,
      periodOptions,
      topVendorOptions,
      period.value,
      period.onChange,
      currencies.fetchOptions,
      currencies.value,
      currencies.onChange,
      vendors.fetchOptions,
      vendors.value,
      vendors.onChange,
      topVendors.value,
      topVendors.onChange,
      getCurrentValues,
      vendorsPlaceholderDescriptor,
    ],
  );
};

export default useFieldProps;
